import { PaymentMethod } from "@app/models/PaymentMethod";
import { PaymentSchedule } from "@app/models/PaymentSchedule";
import { Confirmation } from "@app/models/Confirmation";
import { ValidRouting } from "@app/models/ValidRouting";
import { SimpleMessage } from '@app/models/SimpleMessage';
import { PaymentScheduleApi } from "@app/services/apiServices/PaymentScheduleApi";
import { PropertyApi } from "@app/services/apiServices/PropertyApi";
import { PaymentApi } from "@app/services/apiServices/PaymentApi";
import { Observable } from "rxjs";
import { Inject, Injectable } from "@angular/core";
import { HOAApiBase } from "@app/services/apiServices/utils/HOAApiBase";
import { take } from "rxjs/operators";
import { moneyAdd, moneyNumber } from "@app/util/money-number";
import { PaymentTimingType } from '@app/services/shared/PaymentTimingType.enum';

const StatusType: any = {
    ACCOUNT_BALANCE: 'account-balance',
    FIXED_AMOUNT: 'fixed-amount'
};

@Injectable({ providedIn: "root" })
export class PaymentMethodApi extends HOAApiBase {

    constructor(@Inject(PaymentScheduleApi) private paymentScheduleApi: PaymentScheduleApi,
                @Inject(PropertyApi) private propertyApi: PropertyApi,
                @Inject(PaymentApi) private paymentApi: PaymentApi) {
        super('PaymentMethodApi');

    }

    /**
     * List payment methods in the user's wallet
     * Returns a list of PaymentMethod objects.\n
     * @param id Unique identifier for the user
     */
    public homeownersIdPaymentMethodsGet(id: number): Observable<PaymentMethod[]> {
        if (!id) {
            throw new Error('Missing required parameter id when calling homeownersIdPaymentMethodsGet');
        }

        return this.http.get<PaymentMethod[]>(this.createUrl(this.getBasePath(), `/homeowners/${id}/paymentMethods`));
    }

    /**
     * Verify payment method
     * Determine if the payment method entered is valid.\n
     * @param routingNumber
     * @param isGuest
     */
    public paymentMethodsRoutingNumberGet(routingNumber: string, isGuest: boolean): Observable<ValidRouting> {
        if (!routingNumber) {
            throw new Error('Missing required parameter routingNumber when calling paymentMethodsRoutingNumberGet');
        }

        if (isGuest) {
            return this.http.get<ValidRouting>(this.createUrl(this.getLocalPath(), "/guestPayment/validateRoutingNumber"), {
                params: {
                    routingNumber: routingNumber
                }
            })
        } else {
            return this.http.get<ValidRouting>(this.createUrl(this.getBasePath(), `/paymentMethods/routingNumber/${routingNumber}`))
        }

    }

    /**
     * Delete Payment Method
     * Method to delete a payment method from the wallet (credit/debit card, bank account)\n
     * @param id payment method ID for the payment method
     */
    public paymentMethodsIdDelete(id: number | string): Observable<Confirmation> {
        if (!id) {
            throw new Error('Missing required parameter id when calling paymentMethodsIdDelete');
        }

        return this.http.delete<Confirmation>(this.createUrl(this.getBasePath(), `/paymentMethods/${id}`))
    }

    /**
     * Load Payment Method Detail
     * Load detail for a payment method - credit/debit card or bank account\n
     * @param id Payment Method ID
     */
    public paymentMethodsIdGet(id: number | string): Observable<PaymentMethod> {
        if (!id) {
            throw new Error('Missing required parameter id when calling paymentMethodsIdGet');
        }

        return this.http.get<PaymentMethod>(this.createUrl(this.getBasePath(), `/paymentMethods/${id}`))
    }

    /**
     * Update Payment Method Detail
     * Update payment method data. Either send in a bank account or card.\n
     * @param paymentMethod Payment method - can be either a credit/debit card or bank account
     * @see https://webitg4.mutualofomaha.com/hoa-gateway/swagger-ui.html#!/PaymentMethod/paymentMethodsIdPostUsingPOST
     */
    public paymentMethodsPost(paymentMethod: PaymentMethod): Observable<PaymentMethod> {
        return this.http.post<PaymentMethod>(this.createUrl(this.getBasePath(), `/paymentMethods`), paymentMethod)
    }

    /**
     * List the payment schedules associated with a payment method.
     * Retrieve the list of payment schedules for a payment method.\n
     * @param id Unique identifier for a payment method
     * @param excludeInProcessWindow
     */
    public paymentMethodsIdPaymentSchedulesGet(id: string, excludeInProcessWindow?: boolean): Observable<PaymentSchedule[]> {
        if (!id) {
            throw new Error('Missing required parameter id when calling paymentMethodsIdPaymentSchedulesGet');
        }

        const params = {};

        if (typeof excludeInProcessWindow !== 'undefined') {
            params['excludeInProcessWindow'] = excludeInProcessWindow;
        }

        return this.http.get<PaymentSchedule[]>(this.createUrl(this.getBasePath(), `/paymentMethods/${id}/paymentSchedules`), { params })
    }

    /**
     * Pre-check to determine if a Payment Method is allowed to be updated.\n
     * @param id Unique identifier for a payment method
     */
    public paymentMethodsIdAllowedUpdateGet(id: string): Observable<SimpleMessage> {
        if (!id) {
            throw new Error('Missing required parameter id when calling paymentMethodsIdAllowedUpdateGet');
        }

        return this.http.get<SimpleMessage>(this.createUrl(this.getBasePath(), `/paymentMethods/${id}/allowedUpdate`))
    }

    public getDefaultPaymentMethodSchedules(userId: number): Observable<PaymentSchedule[]> {

        return new Observable<PaymentSchedule[]>((observer) => {
            this.homeownersIdPaymentMethodsGet(userId).pipe(take(1)).subscribe({ next:(resp) => {
                if (resp) {
                    let defaultPaymentMethod = "" as any;

                    for (let p of resp) {
                        if (p.defaultMethod === true) {
                            defaultPaymentMethod = p;
                            break;
                        }
                    }
                    if (defaultPaymentMethod !== '') {
                        this.paymentMethodsIdPaymentSchedulesGet("" + defaultPaymentMethod.id, true)
                            .pipe(take(1)).subscribe({
                                next: resp => observer.next(resp)
                            });
                    } else {
                        observer.next([]);
                    }
                } else {
                    observer.next([]);
                }
            }, error: (err) => {
                observer.error(err)
            } });
        })
    }

    /**
     * Propagate passed payment method to passed payment Schedules
     * @param paymentSchedules
     * @param paymentMethod Credit / debit card
     * @param convFeeRecalc
     */
    public async paymentMethodPropagateToSchedules(paymentSchedules: PaymentSchedule[], paymentMethod: PaymentMethod, convFeeRecalc: boolean): Promise<string[]> {
        let failedPS = [];
        await Promise.all(paymentSchedules.map(async ps => {
            ps.paymentMethod = paymentMethod;
            ps.paymentMethodId = paymentMethod.id;
            ps.remainingPayments = ps.remainingPayments && ps.endDate ? ps.remainingPayments : null;
            ps.endDate = ps.remainingPayments && ps.endDate ? ps.endDate : null
            ps.paymentMethodType = paymentMethod.type;
            if (convFeeRecalc && ps.type !== StatusType.ACCOUNT_BALANCE) {
                await this.propertyApi.propertiesIdGet(ps.propertyId)
                    .pipe(take(1)).subscribe({ next: resp => {
                        // let hoaId = resp.hoa.couponId;
                        // let pmcId = resp.hoa.pmc.couponId;
                        this.paymentApi.convenienceFeePropertyIdAmountGet(
                            false,
                            resp.address.state,  // not used in the method called, but passing anyway
                            resp.address.state,  // not used in the method called, but passing anyway
                            ps.principalAmount,
                            resp.hoa.pmc.couponId,
                            resp.hoa.couponId,
                            paymentMethod.routingNumber != null ? "ACH" : "CARD",
                            PaymentTimingType.SCHEDULED, ps.frequency.toUpperCase()).pipe(take(1)).subscribe(resp => {
                            ps.feeAmount = moneyNumber(resp.convenienceFee);
                            ps.totalAmount = moneyAdd(ps.principalAmount, ps.feeAmount);
                            ps.futurePayments.forEach(fp => {
                                fp.total = moneyNumber(ps.totalAmount);
                                let feeSet = false;
                                fp.items.forEach(lineItem => {
                                    if (lineItem.description === 'fee') {
                                        lineItem.amount = moneyNumber(ps.feeAmount);
                                        feeSet = true;
                                    }
                                })
                                if (!feeSet) {
                                    fp.items.push({
                                        description: 'fee',
                                        amount: moneyNumber(ps.feeAmount),
                                    });
                                }
                            })
                            this.paymentScheduleApi.paymentSchedulesUpdatePost(ps)
                                .pipe(take(1)).subscribe({ next: () => {
                                    Promise.resolve();
                                }, error: () => {
                                    failedPS.push(ps.nickname);
                                    Promise.resolve();
                                } });
                        })
                    },error:  () => {
                        failedPS.push(ps.nickname);
                        Promise.resolve();
                    } });
            } else {
                await this.paymentScheduleApi.paymentSchedulesUpdatePost(ps)
                    .pipe(take(1)).subscribe({ next: () => {
                        Promise.resolve();
                    }, error: () => {
                        failedPS.push(ps.nickname);
                        Promise.resolve();
                    } });
            }
        }))

        return Promise.resolve(failedPS);

    }
}

